import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";

/* De offentlige, indekserbare sider ligger i hovedchunken. En crawler skal kunne få
   hele HTML'en uden først at hente en ekstra JS-chunk, så de må ikke være lazy. */
import CoursesPage from "./pages/CoursesPage";
import CourseGroupDetailPage from "./pages/CourseGroupDetailPage";
import CourseCalendarPage from "./pages/CourseCalendarPage";
import AboutPage from "./pages/AboutPage";
import UnemployedPage from "./pages/UnemployedPage";
import JobcenterPage from "./pages/JobcenterPage";
import PartnerPage from "./pages/PartnerPage";
import ContactPage from "./pages/ContactPage";
import UsefulLinksPage from "./pages/UsefulLinksPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import UdliciteringPage from "./pages/UdliciteringPage";
import AddressesPage from "./pages/AddressesPage";
import LocationsPage from "./pages/LocationsPage";
import LocationDetailPage from "./pages/LocationDetailPage";
import KnowledgePage from "./pages/KnowledgePage";
import VognmandOnboarding from "./pages/VognmandOnboarding";
import KnowledgeArticlePage from "./pages/KnowledgeArticlePage";
import LangIndexPage from "./pages/LangIndexPage";
import LangCoursePage from "./pages/LangCoursePage";
import BusinessPage from "./pages/BusinessPage";
import BusinessSubPage from "./pages/business/BusinessSubPage";
import PerspektivArticle from "./pages/PerspektivArticle";
import FvuLanding from "./pages/FvuLanding";
import NotFoundPage from "./pages/NotFoundPage";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import SiteSchema from "./components/SiteSchema";

/* Alt bag login, plus noindex-værktøjssiderne, hentes først når ruten rammes.
   Før lå admin, CRM, VP, VP-Flex, portalerne og FVU-appen i samme chunk som
   forsiden, så /kurser hentede 1,02 MB JS for kode, ingen crawler må se. */
const CourseDetailPage = lazy(() => import("./pages/CourseDetailPage"));
const PublicDocumentForm = lazy(() => import("./pages/PublicDocumentForm"));
const PublicDocumentEmployerForm = lazy(() => import("./pages/PublicDocumentEmployerForm"));
const PublicQrSignup = lazy(() => import("./pages/PublicQrSignup"));
const PublicEmploymentSign = lazy(() => import("./pages/PublicEmploymentSign"));
const PublicKautionSign = lazy(() => import("./pages/PublicKautionSign"));
const PublicContractSign = lazy(() => import("./pages/PublicContractSign"));
const PublicKycUpload = lazy(() => import("./pages/PublicKycUpload"));
const LeadCaptureForm = lazy(() => import("./pages/LeadCaptureForm"));
const Registrering = lazy(() => import("./pages/Registrering"));
const CourseInterestForm = lazy(() => import("./pages/CourseInterestForm"));
const BusinessAdminDashboard = lazy(() => import("./pages/BusinessAdminDashboard"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const BusinessClientPortal = lazy(() => import("./pages/BusinessClientPortal"));
const ChauffeurDashboard = lazy(() => import("./pages/ChauffeurDashboard"));
const SagkyndigDashboard = lazy(() => import("./pages/SagkyndigDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const MinSkoleLogin = lazy(() => import("./pages/MinSkoleLogin"));
const MinSkoleDashboard = lazy(() => import("./pages/MinSkoleDashboard"));
const MyLearnDashboard = lazy(() => import("./pages/MyLearnDashboard"));
const InstructorCalendar = lazy(() => import("./pages/InstructorCalendar"));
const InstructorMissingSignatures = lazy(() => import("./pages/InstructorMissingSignatures"));
const InstructorTaxa = lazy(() => import("./pages/InstructorTaxa"));
const InstructorHold = lazy(() => import("./pages/InstructorHold"));
const TaxaLektionsplan = lazy(() => import("./pages/TaxaLektionsplan"));
const ActiveHoldsCalendarPage = lazy(() => import("./pages/ActiveHoldsCalendarPage"));
const DokuscanPage = lazy(() => import("./pages/DokuscanPage"));
const StiftelseLanding = lazy(() => import("./pages/Selskabsstiftelse").then(m => ({ default: m.StiftelseLanding })));
const StiftelseLogin = lazy(() => import("./pages/Selskabsstiftelse").then(m => ({ default: m.StiftelseLogin })));
const StiftelseMineSager = lazy(() => import("./pages/Selskabsstiftelse").then(m => ({ default: m.StiftelseMineSager })));
const StiftelseStart = lazy(() => import("./pages/Selskabsstiftelse").then(m => ({ default: m.StiftelseStart })));
const StiftelseCase = lazy(() => import("./pages/Selskabsstiftelse").then(m => ({ default: m.StiftelseCase })));
const GcalSettings = lazy(() => import("./pages/GcalSettings"));
const VotraVP = lazy(() => import("./pages/VotraVP"));
const VotraVPFlex = lazy(() => import("./pages/VotraVPFlex"));
const VpFlexGuide = lazy(() => import("./pages/vpflex/Guide"));
const VpFlexAdmin = lazy(() => import("./pages/VpFlexAdmin"));
const VotraVpAdmin = lazy(() => import("./pages/VotraVpAdmin"));
const FvuApp = lazy(() => import("./pages/FvuApp"));
const FvuAdmin = lazy(() => import("./pages/FvuAdmin"));
const SignRequestPage = lazy(() => import("./pages/SignRequestPage"));

/* Vises kun mens en portal-chunk hentes. De offentlige sider er ikke lazy, så
   crawlerne ser aldrig denne fallback. */
const ChunkFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center bg-white"
    data-testid="route-loading"
  >
    <div
      className="h-9 w-9 animate-spin rounded-full border-4 border-solid border-r-transparent"
      style={{ borderColor: "#0055FF", borderRightColor: "transparent" }}
      role="status"
      aria-label="Indlæser"
    />
  </div>
);
import { Seo, breadcrumbJsonLd, courseCatalogJsonLd, faqJsonLd } from "./components/Seo";
import { COURSE_GROUPS } from "./data/courseGroups";
import { HOME_FAQ, LEDIGE_FAQ, JOBCENTRE_FAQ, KONTAKT_FAQ } from "./data/homeContent";
import { Toaster } from "@/components/ui/sonner";
import CookieConsent from "./components/CookieConsent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/** Redirects browsers hitting the legacy Nemlog-in EntityID URL to the
 * backend endpoint that actually serves the SP metadata XML. */
const NemloginMetadataRedirect = () => {
  React.useEffect(() => {
    window.location.replace(`${API}/nemlogin/metadata`);
  }, []);
  return (
    <div style={{ fontFamily: "system-ui", padding: 40, textAlign: "center" }}>
      Henter Nemlog-in metadata…
    </div>
  );
};

// SEO wrapper for dynamic course-group detail pages (/kurser/:slug).
// Selve Course-schemaet udstilles af siden (den har holdene og priserne) — her sættes
// kun titel, beskrivelse, canonical og brødkrumme, så der aldrig bliver to Course-noder.
const CourseGroupSeoRoute = () => {
  const { slug } = useParams();
  const g = COURSE_GROUPS.find((c) => c.slug === slug);
  const name = g ? (g.title || g.shortTitle) : "Kursus";
  const priceLow = g?.hasTypeVariants
    ? Math.min(...Object.values(g.prices || {}).map((t) => t.price).filter(Boolean))
    : (g?.prices?.da?.standard ?? null);
  const prisTxt = priceLow ? `${priceLow.toLocaleString("da-DK")} kr.` : null;
  // «Varierer efter type» er ikke en varighed og siger intet i et søgeresultat,
  // hverken i titlen eller i beskrivelsen.
  const durTxt = g?.duration && !/varierer/i.test(g.duration) ? g.duration : null;
  const bits = g?.selfStudy
    ? ["online kursus + prøve hos Votra", prisTxt && `fra ${prisTxt}`].filter(Boolean).join(" · ")
    : [durTxt, prisTxt && `pris fra ${prisTxt}`].filter(Boolean).join(" · ");
  // Beskrivelsen blev før skåret hårdt ved 158 tegn, så den sluttede midt i et ord
  // («... og tilmeld dig ») i søgeresultatet. Nu bygges den af to dele: faktadelen,
  // der altid skal med, og en opfordring, der kun tages med i den længde, der er
  // plads til. Bliver faktadelen alene for lang, klippes den ved sidste hele ord.
  const clamp = (s, max = 158) => {
    if (s.length <= max) return s;
    const cut = s.slice(0, s.lastIndexOf(" ", max))
      .replace(/[,.·\s]+$/, "")
      .replace(/[,\s]+(og|eller|samt|med|hos|for|til|på|i|af|som|der)$/i, "");
    return `${cut}.`;
  };
  const head = `${name}: ${bits ? bits + ". " : ""}${g?.shortDescription || g?.description || ""}`
    .replace(/\.\./g, ".").trim();
  const cta = ["Se kommende hold, adgangskrav og tilmeld dig online hos Votra.",
               "Se kommende hold og tilmeld dig online hos Votra.",
               "Se hold, priser og tilmelding."].find((c) => head.length + 1 + c.length <= 158);
  const desc = g
    ? (cta ? `${head} ${cta}` : clamp(head))
    : "Se kursusdetaljer, datoer og priser hos Votra Vognmandsuddannelser.";
  // Google afkorter titler over ca. 60 tegn, så kursusnavnet skal stå først, og
  // salgsargumenterne tages kun med, hvis der er plads til dem. Står der en
  // `seoTitle` på kursusgruppen, bruges den i stedet: nogle sider søges på et andet
  // ord end kursusnavnet (fx «godsvognmandsuddannelse», «taxakursus»).
  const titleBits = g?.selfStudy
    ? [prisTxt && `fra ${prisTxt}`, "selvstudie + prøve"]
    : [durTxt, prisTxt && `fra ${prisTxt}`];
  let title = `${name} | Votra`;
  for (const bit of titleBits.filter(Boolean)) {
    const next = title.replace(" | Votra", `${title.includes(":") ? "," : ":"} ${bit} | Votra`);
    if (next.length <= 60) title = next;
  }
  if (g?.seoTitle) title = g.seoTitle;
  const path = `/kurser/${slug}`;
  return (
    <>
      <Seo
        title={title}
        description={desc}
        path={path}
        jsonLd={[
          breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Kurser", path: "/kurser" }, { name, path }]),
        ]}
      />
      <CourseGroupDetailPage />
    </>
  );
};

// Gamle /business/perspektiver/:slug-URL'er peger nu på vidensuniverset.
const PerspektivRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/business/viden/${slug}`} replace />;
};

export const ProtectedAdminRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('minskoleToken');
  let userType = null;
  try {
    userType = JSON.parse(localStorage.getItem('minskoleUser') || '{}').user_type;
  } catch (e) {
    userType = null;
  }
  // Admin surface is instructor-only — block partner / chauffeur / student tokens
  if (!token || userType !== 'instructor') {
    return <Navigate to="/mitvotra" replace />;
  }
  return children;
};

export const ProtectedStudentRoute = ({ children }) => {
  const token = localStorage.getItem('studentToken') || localStorage.getItem('minskoleToken');
  if (!token) {
    return <Navigate to="/mitvotra" replace />;
  }
  return children;
};

export const ProtectedMinskoleRoute = ({ children }) => {
  const token = localStorage.getItem('minskoleToken');
  if (!token) {
    return <Navigate to="/mitvotra" replace />;
  }
  return children;
};

/** /Business og /BUSINESS/Viden skal ikke være separate URL'er med samme indhold.
 *  Kun /business-grenen normaliseres: de øvrige ruter indeholder tokens og id'er,
 *  hvor store bogstaver er betydende, og de må derfor ikke sænkes. */
const LowercaseBusinessPath = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const lower = pathname.toLowerCase();
  if (lower.startsWith("/business") && pathname !== lower && !pathname.includes("%")) {
    return <Navigate to={`${lower}${search}${hash}`} replace />;
  }
  return children;
};

/* Rutetræet ligger for sig, uden <BrowserRouter>, så build-tidens prerendering kan
   rendre præcis samme træ gennem <StaticRouter> (scripts/ssg/entry.jsx). Det er dét,
   der gør, at enhver klient får sidens indhold i den første HTML-response, også uden
   JavaScript. Toaster og cookiebanneret er bevidst ikke med: de er klient-chrome og
   skal ikke stå i den statiske HTML, en crawler læser som sidens indhold. */
export const AppRoutes = () => {
  return (
    <>
        <ScrollToTop />
        <SiteSchema />
        <LowercaseBusinessPath>
        <Suspense fallback={<ChunkFallback />}>
        <Routes>
          {/* Main landing — countdown finished, show courses */}
          <Route path="/" element={<><Seo title="Vognmandsuddannelse, taxakursus og chaufførkursus | Votra" description="Votra tilbyder vognmandsuddannelser, taxakurser og chaufføruddannelser i hele Danmark. Find dit kursus, se ledige pladser og tilmeld dig online." path="/" jsonLd={[breadcrumbJsonLd([{ name: "Forside", path: "/" }]), faqJsonLd(HOME_FAQ)]} /><CoursesPage variant="home" /></>} />

          {/* Course pages */}
          <Route path="/kurser" element={<><Seo title="Alle kurser: vognmand, taxa, VB GU og BAB | Votra" description="Se alle Votras kurser: vognmand til lastbil og varebil, taxa, grunduddannelse (VB GU/EU), BAB og IT & AI. Tilmeld dig direkte online." path="/kurser" jsonLd={[breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Kurser", path: "/kurser" }]), courseCatalogJsonLd(COURSE_GROUPS)]} /><CoursesPage /></>} />
          <Route path="/kurser/:slug" element={<CourseGroupSeoRoute />} />
          <Route path="/underskriv/:token" element={<SignRequestPage />} />
          <Route path="/course/:courseId" element={<CourseDetailPage />} />
          {/* /kalender og /adresser ejer selv deres <Seo>. Lå den både her og i siden,
              fik Google to canonical-tags på samme side. */}
          <Route path="/kalender" element={<CourseCalendarPage />} />

          {/* Public pages */}
          {/* Brødkrummer på de offentlige enkeltsider, så Google kan vise stien i
              søgeresultatet i stedet for den rå URL. */}
          <Route path="/om-os" element={<><Seo title="Om Votra: din uddannelsespartner i transportbranchen" description="Lær Votra at kende. Vi uddanner fremtidens vognmænd, taxachauffører og chauffører med erfarne undervisere og høj beståelsesprocent." path="/om-os" jsonLd={breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Om os", path: "/om-os" }])} /><AboutPage /></>} />
          <Route path="/ledige" element={<><Seo title="Ledig? Kom i job i transportbranchen | Votra" description="Er du ledig? Votras vognmand-, taxa- og chaufførkurser kan være vejen til job. Se muligheder for tilskud via jobcenter og a-kasse." path="/ledige" jsonLd={[breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Ledige", path: "/ledige" }]), faqJsonLd(LEDIGE_FAQ)]} /><UnemployedPage /></>} />
          <Route path="/jobcentre" element={<><Seo title="Jobcentre og a-kasser: samarbejde om kurser | Votra" description="Votra samarbejder med jobcentre og a-kasser om opkvalificering til transportbranchen. Se hvordan I henviser ledige til vores kurser." path="/jobcentre" jsonLd={[breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Jobcentre og a-kasser", path: "/jobcentre" }]), faqJsonLd(JOBCENTRE_FAQ)]} /><JobcenterPage /></>} />
          <Route path="/partner" element={<><Seo title="Partnerprogram for virksomheder | Votra" description="Bliv partner med Votra. Uddan jeres chauffører og vognmænd med skræddersyede kursusforløb og fast kontaktperson." path="/partner" jsonLd={breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Partnerprogram", path: "/partner" }])} /><PartnerPage /></>} />
          <Route path="/kontakt" element={<><Seo title="Kontakt Votra: ring, skriv eller besøg os" description="Kontakt Votra Vognmandsuddannelser. Ring, send en mail eller besøg en af vores afdelinger. Vi hjælper dig godt videre." path="/kontakt" jsonLd={[breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Kontakt", path: "/kontakt" }]), faqJsonLd(KONTAKT_FAQ)]} /><ContactPage /></>} />
          <Route path="/nyttige-links" element={<><Seo title="Nyttige links: a-kasser og myndigheder | Votra" description="Samling af nyttige links til a-kasser, myndigheder og ressourcer for kommende vognmænd og chauffører." path="/nyttige-links" jsonLd={breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Nyttige links", path: "/nyttige-links" }])} /><UsefulLinksPage /></>} />
          <Route path="/persondata" element={<><Seo title="Persondatapolitik | Votra" description="Læs hvordan Votra Vognmandsuddannelser behandler og beskytter dine personoplysninger." path="/persondata" jsonLd={breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Persondata", path: "/persondata" }])} /><PrivacyPage /></>} />
          <Route path="/kursusbetingelser" element={<><Seo title="Kursusbetingelser | Votra" description="Se Votras kursusbetingelser for tilmelding, betaling og afmelding af kurser." path="/kursusbetingelser" jsonLd={breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Kursusbetingelser", path: "/kursusbetingelser" }])} /><TermsPage /></>} />
          <Route path="/udlicitering" element={<><Seo title="Udlicitering af AMU-kurser | Votra" description="Votra udbyder AMU-kurser i samarbejde med godkendte uddannelsessteder. Se aktuelle udliciterede vognmand- og flextrafik-kurser." path="/udlicitering" jsonLd={breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Udlicitering", path: "/udlicitering" }])} /><UdliciteringPage /></>} />
          <Route path="/adresser" element={<AddressesPage />} />
          <Route path="/lokationer" element={<LocationsPage />} />
          <Route path="/aktive-hold-kalender" element={<ActiveHoldsCalendarPage />} />
          <Route path="/dokuscan" element={<DokuscanPage />} />
          <Route path="/dokuscan/:nr" element={<DokuscanPage />} />
          <Route path="/lokationer/:slug" element={<LocationDetailPage />} />
          <Route path="/viden" element={<KnowledgePage />} />
          <Route path="/viden/:slug" element={<KnowledgeArticlePage />} />
          {/* Sprogsider — kun de forløb der faktisk kan tages på fremmedsprog.
              Taxa er med som ærlig oplysningsside (dansk er et lovkrav). */}
          <Route path="/en" element={<LangIndexPage lang="en" />} />
          <Route path="/ar" element={<LangIndexPage lang="ar" />} />
          <Route path="/uk" element={<LangIndexPage lang="uk" />} />
          <Route path="/en/:slug" element={<LangCoursePage lang="en" />} />
          <Route path="/ar/:slug" element={<LangCoursePage lang="ar" />} />
          <Route path="/uk/:slug" element={<LangCoursePage lang="uk" />} />
          <Route path="/dokument/udfyld/:id" element={<><Seo title="Udfyld dokument | Votra" description="Udfyld og underskriv dokument digitalt." path="/dokument/udfyld" noindex /><PublicDocumentEmployerForm /></>} />
          <Route path="/dokument/:slug" element={<><Seo title="Dokument | Votra" description="Udfyld og underskriv dokument digitalt." path="/dokument" noindex /><PublicDocumentForm /></>} />
          <Route path="/ansaettelse/sign/:token" element={<><Seo title="Underskriv ansættelseskontrakt | Votra" description="Underskriv din ansættelseskontrakt digitalt." path="/ansaettelse/sign" noindex /><PublicEmploymentSign /></>} />
          <Route path="/laan/kaution/:token" element={<><Seo title="Underskriv personlig kaution | Votra" description="Underskriv personlig selvskyldnerkaution digitalt." path="/laan/kaution" noindex /><PublicKautionSign /></>} />
          <Route path="/kontrakt/sign/:token" element={<><Seo title="Underskriv kontrakt | Votra" description="Underskriv din kontrakt digitalt." path="/kontrakt/sign" noindex /><PublicContractSign /></>} />
          <Route path="/kyc/upload/:token" element={<><Seo title="Upload KYC-dokumenter | Votra" description="Upload dine dokumenter sikkert til Votra." path="/kyc/upload" noindex /><PublicKycUpload /></>} />
          <Route path="/vp" element={<><Seo title="Votra VP: vognmandsportal" description="Log ind i Votra Vognmandsportal og se dine Taxi Nord-kørselsdata i et pænt overblik." path="/vp" noindex /><VotraVP /></>} />
          <Route path="/vp-flex" element={<><Seo title="Votra VP-Flex: Movia Flextrafik" description="Log ind i Votra VP-Flex og administrer dine Movia Flextrafik-ruter, vognløb og privatrejser." path="/vp-flex" noindex /><VotraVPFlex /></>} />
          <Route path="/vp-flex/vejledning/:type" element={<><Seo title="Vejledning til Votra VP-Flex" description="Trin-for-trin vejledning til VP-Flex for kørselskontorer og underleverandører." path="/vp-flex/vejledning" /><VpFlexGuide /></>} />
          <Route path="/vp-flex-admin" element={<><Seo title="VP-Flex Administration | Votra" description="Administrer kørselskontorer og underleverandører i Votra VP-Flex." path="/vp-flex-admin" noindex /><VpFlexAdmin /></>} />
          <Route path="/lead" element={<><Seo title="Kontakt Votra Business" description="Udfyld og kontakt Votra Business." path="/lead" noindex /><LeadCaptureForm /></>} />
          <Route path="/registrering" element={<><Seo title="Registrering | Votra" description="Tjek ind og ud på dit taxa-hold." path="/registrering" noindex /><Registrering /></>} />
          {/* En tynd formularside, der konkurrerede med kursussiderne i søgeresultatet.
              Den skal kunne bruges af folk, der får linket, men ikke indekseres. */}
          <Route path="/tilmeldingsblanket" element={<><Seo title="Tilmeldingsblanket | Votra" description="Tilmeld dig et kursus hos Votra. Udfyld blanketten med navn, email, telefon og hvilket kursus du er interesseret i." path="/tilmeldingsblanket" noindex /><CourseInterestForm /></>} />
          {/* Klassisk crawl-adresse. Uden denne rute endte /index.html på 404-siden. */}
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          {/* Votra Business 2.0 — forside, undersider og vidensunivers.
              Hver side ejer selv sin Seo/schema (se pages/business/BusinessSubPage.jsx). */}
          <Route path="/vognmand-onboarding" element={<><Seo title="Bliv vognmand: beregn din indtjening | Votra" description="Vil du starte som vognmand? Beregn arbejdstimer, omkostninger, break-even og forventet indtjening med Votras gratis vognmandsberegner." path="/vognmand-onboarding" jsonLd={[breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Vognmand onboarding", path: "/vognmand-onboarding" }])]} /><VognmandOnboarding /></>} />
          <Route path="/business" element={<BusinessPage />} />
          <Route path="/business/selskabsstiftelse" element={<StiftelseLanding />} />
          <Route path="/business/selskabsstiftelse/start" element={<StiftelseLogin />} />
          <Route path="/mit-votra/login" element={<StiftelseLogin />} />
          <Route path="/mit-votra/start" element={<StiftelseStart />} />
          <Route path="/mit-votra/mine-sager" element={<StiftelseMineSager />} />
          <Route path="/mit-votra/mine-sager/:id" element={<StiftelseCase />} />
          <Route path="/business/viden/:slug" element={<PerspektivArticle />} />
          <Route path="/business/perspektiver/:slug" element={<PerspektivRedirect />} />
          <Route path="/business/:slug" element={<BusinessSubPage />} />

          {/* FVU Dansk trin 2 — offentligt produktsite + licens-låst forløb */}
          <Route path="/fvu" element={<><Seo title="FVU Dansk trin 2: AI-drevet forberedelse | Votra" description="Bliv klar til FVU-dansk trin 2 med Votras AI-tutor på 12 sprog, oplæsning og realistiske øveprøver. 12 moduler, 60 timer." path="/fvu" jsonLd={breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "FVU Dansk trin 2", path: "/fvu" }])} /><FvuLanding /></>} />
          <Route path="/fvu/app" element={<><Seo title="Votra · Dansk trin 2" description="Dit FVU-dansk trin 2-forløb." path="/fvu/app" noindex /><ErrorBoundary><FvuApp /></ErrorBoundary></>} />

          {/* Public QR signup (scanned from partner QR code) */}
          <Route path="/tilmeld/:qrToken" element={<><Seo title="Tilmelding | Votra" description="Tilmeld dig holdet via QR-koden." path="/tilmeld" noindex /><PublicQrSignup /></>} />

          {/* Nemlog-in SP metadata — redirect legacy /nemlogin/metadata URL
              to the backend endpoint that actually serves the XML.
              (EntityID now uses /api/nemlogin/metadata but old uploads may
              still reference the shorter URL.) */}
          <Route
            path="/nemlogin/metadata"
            element={<NemloginMetadataRedirect />}
          />

          {/* Business admin panel (scoped, separate from /admin) */}
          <Route
            path="/business-panel/*"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <BusinessAdminDashboard />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />

          {/* Partner platform (separate scope, login starts with P) */}
          <Route
            path="/partnerportal/*"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <PartnerDashboard />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />

          {/* Business consultant platform (login starts with B) */}
          <Route
            path="/business-portal/*"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <BusinessClientPortal />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          
          {/* mitVotra unified login */}
          <Route path="/mitvotra" element={<><Seo title="Log ind på MitVotra" description="Log ind på MitVotra, elev-, partner- og virksomhedsportal." path="/mitvotra" noindex /><MinSkoleLogin /></>} />
          <Route path="/minskole" element={<Navigate to="/mitvotra" replace />} />
          <Route
            path="/mitvotra/dashboard"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <MinSkoleDashboard />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          <Route
            path="/minskole/dashboard"
            element={<Navigate to="/mitvotra/dashboard" replace />}
          />
          <Route
            path="/mylearn"
            element={
              <ProtectedStudentRoute>
                <ErrorBoundary>
                  <MyLearnDashboard />
                </ErrorBoundary>
              </ProtectedStudentRoute>
            }
          />
          <Route
            path="/instructor/calendar"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <InstructorCalendar />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          <Route
            path="/instructor/missing-signatures"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <InstructorMissingSignatures />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          <Route
            path="/instructor/taxa"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <InstructorTaxa />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          <Route
            path="/instructor/hold"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <InstructorHold />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          <Route
            path="/mitvotra/gcal"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <GcalSettings />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          <Route
            path="/mitvotra/votra-vp"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <VotraVpAdmin />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          <Route
            path="/mitvotra/fvu"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <FvuAdmin />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          <Route
            path="/mitvotra/lektionsplan"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <TaxaLektionsplan />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          
          {/* Legacy admin routes */}
          <Route path="/admin/login" element={<Navigate to="/mitvotra" replace />} />
          <Route
            path="/admin/*"
            element={
              <ProtectedAdminRoute>
                <ErrorBoundary>
                  <AdminDashboard />
                </ErrorBoundary>
              </ProtectedAdminRoute>
            }
          />
          
          {/* Legacy student routes */}
          <Route path="/student/login" element={<Navigate to="/mitvotra" replace />} />
          <Route
            path="/student/*"
            element={
              <ProtectedStudentRoute>
                <ErrorBoundary>
                  <StudentDashboard />
                </ErrorBoundary>
              </ProtectedStudentRoute>
            }
          />

          {/* Chauffør dashboard (C-prefix login) */}
          <Route
            path="/chauffor/*"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <ChauffeurDashboard />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          {/* Sagkyndig dashboard (S-prefix login) */}
          <Route
            path="/sagkyndig/*"
            element={
              <ProtectedMinskoleRoute>
                <ErrorBoundary>
                  <SagkyndigDashboard />
                </ErrorBoundary>
              </ProtectedMinskoleRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </LowercaseBusinessPath>
    </>
  );
};

const AiChatWidget = React.lazy(() => import("./components/AiChatWidget"));

/* AI-chatten ligger uden for <AppRoutes>, så den ikke står i den prerenderede HTML:
   den er klient-chrome, ikke sidens indhold. Den skjuler sig selv på de private
   ruter og vælger vidensgrundlag efter stien. */
const PRIVATE_STIER = [
  "/admin", "/mitvotra", "/minskole", "/mylearn", "/business-panel", "/business-portal",
  "/vp", "/vp-flex", "/vp-flex-admin", "/dokument", "/tilmeld", "/tilmeldingsblanket", "/underskriv",
  "/kontrakt", "/ansaettelse", "/laan", "/kyc", "/registrering", "/lead", "/fvu/app", "/student",
];

const AiChat = () => {
  const { pathname } = useLocation();
  if (PRIVATE_STIER.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return null;
  return (
    <Suspense fallback={null}>
      <AiChatWidget scope={pathname.startsWith("/business") ? "business" : "skole"} />
    </Suspense>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <AppRoutes />
        <AiChat />
        {/* Cookiebanneret ligger EFTER ruterne, så sidens eget "Spring til indhold"
            er det første element tastaturbrugeren rammer med Tab. */}
        <CookieConsent />
      </BrowserRouter>
    </div>
  );
}

export default App;
